import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import { ScreenPlate } from '../../components/ScreenPlate';
import { useMatchStore } from '../../stores/match';
import { useMatchesStore, parkCurrent, MATCHES_CAP } from '../../stores/matches';
import { useMotionTier } from '../../components/useMotionTier';
import { useLayoutMode } from '../../components/useLayoutMode';
import { useTransitionStore } from '../../stores/transition';
import { EDGES } from '../../theme/tokens';
import { duration, ease, spring } from '../../theme/motion';
import { sfx } from '../../audio/sfx';

// Lobby — for now only one real action (Single Player vs AI).
// Multiplayer / Quick Match lands in R6 / S24-equivalent.

export function PlayPage() {
  const navigate = useNavigate();
  const newMatch = useMatchStore((s) => s.newMatch);
  const openCount = useMatchesStore((s) => s.countOpen());
  const capReached = openCount >= MATCHES_CAP;
  const [capError, setCapError] = useState<string | null>(null);
  const tier = useMotionTier();
  const reduced = tier === 'reduced';
  const { mode } = useLayoutMode();
  const portrait = mode === 'phonePortrait';

  function startSinglePlayer() {
    if (capReached) {
      sfx.error();
      setCapError(
        `You already have ${openCount} matches running — finish or drop one before starting another.`,
      );
      return;
    }
    // "You are now in a match" is the most meaningful state change in the
    // product, and it used to be a bare navigate. It gets the same reel advance
    // as the steps that follow, so the whole run opens as one continuous reel.
    const go = () => {
      // Park whatever's currently loaded so the soon-to-be-replaced active
      // match doesn't get lost when newMatch() overwrites the singleton.
      parkCurrent();
      const matchId = newMatch();
      navigate(`/game/${matchId}/hero`);
    };
    const started = useTransitionStore.getState().start({
      kind: 'advance',
      commit: go,
      payload: { carry: 'Round 1' },
    });
    if (!started) go();
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* The /menu wall again, framed on the lamp end and held back a touch less
          than on the menu — this screen carries only two big CTAs, so it can
          afford more picture. Reusing one plate across both screens is deliberate:
          it reads as the same place, one step further in. */}
      <ScreenPlate src="plates/parking_lot.webp" scrim={0.72} position="right 65%" />

      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/3 top-0 h-[60vw] w-[60vw] max-h-[640px] max-w-[640px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.18) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-5xl flex-col gap-xs overflow-hidden px-md py-xs md:gap-lg md:px-lg md:py-md lg:max-w-6xl lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        <div className="flex flex-1 flex-col justify-center gap-xs md:gap-xl">
          <div>
            <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
              /play
            </p>
            <h1 className="mt-xs font-cholo text-[22px] leading-none text-sodium sm:text-[28px] md:text-[40px] lg:text-[60px]">
              Lobby
            </h1>
            <p className="mt-xs hidden text-[15px] text-marker md:block md:text-[18px] lg:max-w-3xl lg:text-[22px]">
              Choose your run. AI fills the lobby — 8-player free-for-all, last
              crew on the wall wins.
            </p>
          </div>

          {/* Matches hub link — only render when the player has at least
              one in-flight match. Compact strip above the start CTAs so
              they can jump back into a parked game without going through
              the menu. */}
          {openCount > 0 && (
            <Link
              to="/matches"
              className="flex items-center justify-between rounded-md border bg-stucco1 px-md py-sm font-data text-[11px] uppercase tracking-[0.2em] text-marker transition-colors hover:bg-stucco2 hover:text-bone md:px-lg md:py-md md:text-[13px]"
              style={{ borderColor: EDGES.soft }}
            >
              <span>
                <span className="text-sodium">{openCount}</span>{' '}
                {openCount === 1 ? 'match' : 'matches'} in flight · resume →
              </span>
              <span className="text-graphite" aria-hidden>
                {openCount}/{MATCHES_CAP}
              </span>
            </Link>
          )}

          {capError && (
            <p
              role="alert"
              className="rounded-sm border border-aerosolRed/40 bg-aerosolRed/10 px-md py-sm font-data text-[11px] uppercase tracking-[0.2em] text-aerosolRed md:text-[12px]"
            >
              {capError}
            </p>
          )}

          {/* Portrait stacks: side-by-side at 390px gives each CTA ~165px,
              which truncated "Async Multiplayer · coming r6" mid-word.
              Landscape keeps 2-col (width to spare, no height); lg+ stacks
              again for emphasis at desktop scale. */}
          <div
            className={`grid gap-sm md:gap-md lg:grid-cols-1 ${
              mode === 'phonePortrait' ? 'grid-cols-1' : 'grid-cols-2'
            }`}
          >
            <motion.button
              onClick={startSinglePlayer}
              disabled={capReached}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                reduced
                  ? { duration: duration.tap }
                  : { duration: duration.enter, ease: ease.out }
              }
              whileHover={capReached || reduced ? undefined : { scale: 1.02 }}
              // Spring scoped to the gesture, so the primary action in the
              // lobby has mass behind the press without overriding the
              // entrance tween above.
              whileTap={capReached ? undefined : { scale: 0.97, transition: spring.stiff }}
              className={`group flex items-center rounded-md border bg-stucco1 text-left transition-colors sm:min-h-[52px] sm:px-md sm:py-xs md:min-h-[80px] md:gap-md md:px-lg md:py-md lg:min-h-[104px] lg:gap-lg lg:px-xl lg:py-lg ${
                portrait
                  ? 'min-h-[104px] gap-md px-md py-md'
                  : 'min-h-[44px] gap-sm px-sm py-[3px]'
              } ${capReached ? 'cursor-not-allowed opacity-60' : 'hover:bg-stucco2'}`}
              style={{ borderColor: EDGES.soft }}
            >
              <span
                className={`flex shrink-0 items-center justify-center rounded-sm bg-sodium font-data text-sharpie sm:h-10 sm:w-10 sm:text-[18px] md:h-16 md:w-16 md:text-[28px] lg:h-20 lg:w-20 lg:text-[34px] ${
                  portrait ? 'h-16 w-16 text-[28px]' : 'h-9 w-9 text-[16px]'
                }`}
                aria-hidden
              >
                ▶
              </span>
              <span className="flex flex-1 flex-col leading-tight">
                <span className={`font-cholo text-sodium sm:text-[18px] md:text-[32px] lg:text-[44px] ${
                    portrait ? 'text-[30px]' : 'text-[16px]'
                  }`}>
                  Single Player
                </span>
                <span className={`font-data uppercase tracking-[0.2em] text-marker sm:text-[10px] md:text-[12px] lg:text-[14px] ${
                    portrait ? 'text-[12px]' : 'text-[9px]'
                  }`}>
                  vs. 7 ai opponents
                </span>
              </span>
              <span
                className={`font-data text-graphite transition-colors group-hover:text-sodium sm:text-[14px] md:text-[20px] lg:text-[26px] ${
                  portrait ? 'text-[22px]' : 'text-[12px]'
                }`}
                aria-hidden
              >
                ›
              </span>
            </motion.button>

            <div
              className={`flex items-center rounded-md border bg-stucco1 opacity-50 sm:min-h-[52px] sm:px-md sm:py-xs md:min-h-[80px] md:gap-md md:px-lg md:py-md lg:min-h-[104px] lg:gap-lg lg:px-xl lg:py-lg ${
                portrait
                  ? 'min-h-[104px] gap-md px-md py-md'
                  : 'min-h-[44px] gap-sm px-sm py-[3px]'
              }`}
              style={{ borderColor: EDGES.faint }}
            >
              <span
                className={`flex shrink-0 items-center justify-center rounded-sm bg-stucco3 font-data text-graphite sm:h-10 sm:w-10 sm:text-[16px] md:h-16 md:w-16 md:text-[24px] lg:h-20 lg:w-20 lg:text-[30px] ${
                  portrait ? 'h-16 w-16 text-[24px]' : 'h-9 w-9 text-[14px]'
                }`}
                aria-hidden
              >
                ◆
              </span>
              <span className="flex flex-1 flex-col leading-tight">
                <span className={`font-cholo text-graphite sm:text-[16px] md:text-[28px] lg:text-[38px] ${
                    portrait ? 'text-[26px]' : 'text-[14px]'
                  }`}>
                  Async Multiplayer
                </span>
                <span className={`font-data uppercase tracking-[0.2em] text-graphite sm:text-[10px] md:text-[12px] lg:text-[14px] ${
                    portrait ? 'text-[12px]' : 'text-[9px]'
                  }`}>
                  coming r6
                </span>
              </span>
            </div>
          </div>
        </div>

        <footer className="mt-auto text-center font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[12px] lg:text-[13px]">
          v2 · {new Date().getFullYear()} · serifeus studio
        </footer>
      </section>
    </main>
  );
}
